import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirms } from '../hooks/useFirms';
import { sortFirms, filterFirms, getUniqueCities } from '../utils/helpers';
import { exportFirmsToCSV } from '../utils/exportUtils';
import firmService from '../services/firmService';
import AppShell from './shell/AppShell';
import FilterSelect from './shell/FilterSelect';
import BulkActionBar from './shell/BulkActionBar';
import FirmList from './FirmList';
import FirmDetail from './FirmDetail';
import FirmTagsManager from './FirmTagsManager';
import FirmModal from './FirmModal';
import ImportModal from './ImportModal';
import { API_URL } from '../config/api';
import DedupFirmsModal from './DedupFirmsModal';

const FirmsPage = () => {
  const { firms, loading, error, createFirm, updateFirm, deleteFirm, refetch } = useFirms();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'tags' ? 'tags' : 'firms'); // 'firms' | 'tags'
  const [selectedCities, setSelectedCities] = useState([]);
  const [employeeMin, setEmployeeMin] = useState('');
  const [employeeMax, setEmployeeMax] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [dedupModalOpen, setDedupModalOpen] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedFirmId, setSelectedFirmId] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });
  const cancelSyncRef = useRef(false);

  // Mini sidebar: strategy filter

  const allCities = useMemo(() => getUniqueCities(firms), [firms]);

  const filteredAndSortedFirms = useMemo(() => {
    const filtered = filterFirms(firms, {
      searchTerm, selectedCities, employeeMin, employeeMax,
    });
    return sortFirms(filtered, sortField, sortDirection);
  }, [firms, searchTerm, selectedCities, employeeMin, employeeMax, sortField, sortDirection]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    count += selectedCities.length;
    if (employeeMin) count++;
    if (employeeMax) count++;
    return count;
  }, [searchTerm, selectedCities, employeeMin, employeeMax]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedCities([]);
    setEmployeeMin('');
    setEmployeeMax('');
  };

  const handleAddClick = () => { setEditingFirm(null); setDedupModalOpen(false); setModalOpen(true); };
  const handleEditClick = (firm) => { setEditingFirm(firm); setDedupModalOpen(false); setModalOpen(true); };
  const handleExpandFirm = (firm) => { navigate(`/firm/${firm.id}`); };
  
  const handleToggleSelect = (firmId, index, shiftKey) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedIndex !== null && index != null) {
        const [a, b] = [lastSelectedIndex, index].sort((x, y) => x - y);
        for (let i = a; i <= b; i++) {
          const f = filteredAndSortedFirms[i];
          if (f) next.add(f.id);
        }
      } else {
        if (next.has(firmId)) next.delete(firmId);
        else next.add(firmId);
      }
      return next;
    });
    if (!shiftKey && index != null) setLastSelectedIndex(index);
  };
  
  const handleBulkSyncLinkedIn = async () => {
    const firmsToSync = filteredAndSortedFirms.filter(f => selectedIds.has(f.id) && f.linkedinUrl);
    const skipped = selectedIds.size - firmsToSync.length;
    
    if (firmsToSync.length === 0) {
      alert('None of the selected firms have a LinkedIn URL. Add LinkedIn URLs to firms first.');
      return;
    }
    
    const confirmMsg = `Sync ${firmsToSync.length} firms from LinkedIn?` + 
      (skipped > 0 ? `\n\n${skipped} firm${skipped !== 1 ? 's' : ''} will be skipped (no LinkedIn URL).` : '') +
      `\n\nThis will fill only blank fields. Existing data will be preserved.\n\nFirms recently synced (within the org cooldown period) will be skipped automatically to save credits.\n\nAll firms will sync in parallel. Each firm's data is saved immediately when it completes — you can cancel at any time and keep the results from firms that already finished.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    cancelSyncRef.current = false;
    setBulkSyncing(true);
    setSyncProgress({ current: 0, total: firmsToSync.length });
    
    const token = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    let completedCount = 0;
    let successCount = 0;
    let failCount = 0;
    let cooldownCount = 0;
    const errors = [];
    
    // Helper: trigger + poll a single firm independently. Refreshes the firms list on success.
    const syncFirm = async (firm) => {
      try {
        // Trigger
        const triggerRes = await fetch(`${API_URL}/firms/${firm.id}/sync-linkedin-trigger`, {
          method: 'POST', headers, body: JSON.stringify({})
        });
        
        // Cooldown — skip silently, count separately
        if (triggerRes.status === 429) {
          const d = await triggerRes.json().catch(() => ({}));
          if (d.cooldown) {
            cooldownCount++;
            completedCount++;
            setSyncProgress({ current: completedCount, total: firmsToSync.length });
            return;
          }
        }
        
        if (!triggerRes.ok) {
          const d = await triggerRes.json().catch(() => ({}));
          throw new Error(d.detail || d.error || `HTTP ${triggerRes.status}`);
        }
        const { snapshot_id, url } = await triggerRes.json();
        
        // Poll up to 8 minutes
        const maxAttempts = 96;
        const pollInterval = 5000;
        for (let i = 0; i < maxAttempts; i++) {
          if (cancelSyncRef.current) throw new Error('Cancelled by user');
          
          await new Promise(r => setTimeout(r, pollInterval));
          
          if (cancelSyncRef.current) throw new Error('Cancelled by user');
          
          const params = new URLSearchParams({ snapshot_id, overwrite: 'false', url });
          const statusRes = await fetch(`${API_URL}/firms/${firm.id}/sync-linkedin-status?${params}`, { headers });
          const statusData = await statusRes.json();
          
          if (statusData.status === 'ready') {
            successCount++;
            completedCount++;
            setSyncProgress({ current: completedCount, total: firmsToSync.length });
            // Refresh the list immediately so the user sees the new data
            refetch?.();
            return;
          }
          if (statusData.status === 'failed') {
            throw new Error(statusData.error || statusData.detail || 'Failed');
          }
          // 'running' — keep polling
        }
        throw new Error('Timed out after 8 minutes');
      } catch (err) {
        failCount++;
        completedCount++;
        errors.push(`${firm.name}: ${err.message}`);
        setSyncProgress({ current: completedCount, total: firmsToSync.length });
      }
    };
    
    // Fire all firms in parallel — each one resolves independently
    await Promise.all(firmsToSync.map(syncFirm));
    
    cancelSyncRef.current = false;
    setBulkSyncing(false);
    setSyncProgress({ current: 0, total: 0 });
    setSelectedIds(new Set());
    
    let msg = `Bulk sync complete!\n\n✓ ${successCount} synced successfully`;
    if (cooldownCount > 0) msg += `\n⏱ ${cooldownCount} skipped (cooldown — recently synced)`;
    if (failCount > 0) msg += `\n✗ ${failCount} failed or cancelled`;
    if (errors.length > 0 && errors.length <= 5) msg += `\n\nErrors:\n${errors.join('\n')}`;
    else if (errors.length > 5) msg += `\n\nFirst 5 errors:\n${errors.slice(0, 5).join('\n')}`;
    alert(msg);
    
    refetch?.();
  };
  
  const handleCancelBulkSync = () => {
    if (window.confirm('Cancel remaining syncs? Firms that have already completed will keep their data.')) {
      cancelSyncRef.current = true;
    }
  };

  const handleSaveFirm = async (firmData) => {
    if (editingFirm) { await updateFirm(editingFirm.id, firmData); }
    else { await createFirm(firmData); }
    setModalOpen(false);
    setEditingFirm(null);
  };

  const handleDeleteFirm = async (id) => {
    await deleteFirm(id);
    setModalOpen(false);
    setEditingFirm(null);
  };

  const handleCloseModal = () => { setModalOpen(false); setEditingFirm(null); };

  const handleImport = async (firmsData) => {
    try {
      await firmService.bulkCreateFirms(firmsData);
      setImportModalOpen(false);
      window.location.reload();
    } catch (err) {
      throw err;
    }
  };

  // ── PAGE STRIP ────────────────────────────────────────────────────────
  // Horizontal category pills replace the old DraggableMiniSidebar.
  // "All Firms" pill acts as the previous topButton; strategy pills
  // follow. Export button on the right of the strip (was the sidebar's
  // onExport handler). Strategies can be up to 12 — overflow-x-auto so
  // the strip scrolls horizontally if the viewport can't fit them.
  const strategyStrip = (
    <div className="h-10 flex items-stretch px-3 gap-1">
      <button
        onClick={() => { setActiveTab('firms'); setSearchParams({}, { replace: true }); }}
        className={`flex-shrink-0 flex items-center gap-1.5 px-3 text-xs font-medium transition-colors border-b-2 -mb-px rounded-t ${
          activeTab === 'firms'
            ? 'text-emerald-700 border-emerald-600 font-semibold'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100/70'
        }`}
      >
        All Firms
      </button>
      <button
        onClick={() => { setActiveTab('tags'); setSearchParams({ tab: 'tags' }, { replace: true }); }}
        className={`flex-shrink-0 flex items-center gap-1.5 px-3 text-xs font-medium transition-colors border-b-2 -mb-px rounded-t ${
          activeTab === 'tags'
            ? 'text-emerald-700 border-emerald-600 font-semibold'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100/70'
        }`}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.99 1.99 0 013 12V7a4 4 0 014-4z" /></svg>
        Tags
      </button>
    </div>
  );

  const pageStrip = selectedIds.size > 0 ? (
    <BulkActionBar
      count={selectedIds.size}
      onClear={() => setSelectedIds(new Set())}
      busy={bulkSyncing}
      actions={[
        { label: bulkSyncing ? `Syncing ${syncProgress.current}/${syncProgress.total}...` : `Sync ${selectedIds.size} from LinkedIn`, onClick: handleBulkSyncLinkedIn },
        { label: 'Export CSV', onClick: () => exportFirmsToCSV(filteredAndSortedFirms.filter(f => selectedIds.has(f.id))) },
        ...(bulkSyncing ? [{ label: 'Cancel', onClick: handleCancelBulkSync, danger: true }] : []),
      ]}
    />
  ) : strategyStrip;

  return (
    <AppShell pageStrip={pageStrip}>
      {activeTab === 'tags' ? (
        <FirmTagsManager />
      ) : (
      <div className="h-full flex overflow-hidden bg-[color:var(--list-page-bg)]">

        {/* Filters Sidebar — full-height left panel (like the Add/Dedup sidebars) */}
        {showMoreFilters && (
          <div className="w-56 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto animate-[slidein_180ms_ease-out]">
            <style>{`@keyframes slidein { from { opacity: 0; transform: translateX(-12px);} to { opacity: 1; transform: translateX(0);} }`}</style>
            <div className="px-3 py-2.5 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Filters</span>
                <button onClick={() => setShowMoreFilters(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100" title="Close">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="text-[10px] font-medium text-gray-500 mb-1.5">Employees</div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    defaultValue={employeeMin}
                    onBlur={(e) => { if (e.target.value !== (employeeMin || '')) setEmployeeMin(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Min"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  <span className="text-[10px] text-gray-400">—</span>
                  <input
                    type="number"
                    defaultValue={employeeMax}
                    onBlur={(e) => { if (e.target.value !== (employeeMax || '')) setEmployeeMax(e.target.value); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Max"
                    className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="bg-[color:var(--list-page-bg)] px-3 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Filters — opens the left filter sidebar */}
              <button
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                className={`h-[30px] px-2.5 text-sm border rounded flex items-center gap-1 ${
                  showMoreFilters || activeFilterCount > 2
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Filters
                {activeFilterCount > 2 && (
                  <span className="bg-emerald-600 text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>
                )}
              </button>

              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Search..."
                  className="h-[30px] w-44 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
                  style={searchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
                />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Location filter */}
              <FilterSelect
                label="Location"
                value={selectedCities.length === 1 ? selectedCities[0] : ''}
                defaultValue=""
                onChange={(v) => setSelectedCities(v === '' ? [] : [v])}
                minWidth={140}
                options={[{ value: '', label: 'All Locations' }, ...allCities.map(c => ({ value: c, label: c }))]}
              />

              {/* Sort */}
              <FilterSelect
                label="Sort"
                value={`${sortField}-${sortDirection}`}
                defaultValue="name-asc"
                onChange={(v) => { const [f, d] = v.split('-'); setSortField(f); setSortDirection(d); }}
                minWidth={140}
                options={[
                  { value: 'name-asc', label: 'Name A-Z' },
                  { value: 'name-desc', label: 'Name Z-A' },
                  { value: 'totalEmployees-desc', label: 'Most Employees' },
                  { value: 'totalEmployees-asc', label: 'Fewest Employees' },
                  { value: 'bonusDate-asc', label: 'Bonus Date ↑' },
                  { value: 'bonusDate-desc', label: 'Bonus Date ↓' },
                ]}
              />

              {activeFilterCount > 0 && (
                <button onClick={handleClearFilters} className="text-[10px] text-red-500 hover:text-red-700">Clear all</button>
              )}

              <div className="flex-1" />

              {/* Action Buttons */}
              <button
                onClick={() => { setModalOpen(false); setDedupModalOpen(true); }}
                className="h-[30px] px-2.5 text-sm bg-amber-100 border border-amber-300 rounded text-amber-800 hover:bg-amber-200 flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Deduplicate
              </button>

              <button
                onClick={handleAddClick}
                className="h-[30px] px-3 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Firm
              </button>
            </div>

          </div>

          {/* Master–detail area: card list (reflows full-width → rail) + detail pane */}
          <div className="flex-1 min-h-0 flex overflow-hidden">

            {/* List column — full-width when nothing selected, rail when a firm is open */}
            <div className={`flex flex-col overflow-hidden min-h-0 ${selectedFirmId ? 'w-80 flex-shrink-0' : 'flex-1 min-w-0'}`}>
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : error ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-sm text-red-600">Error: {error}</div>
                </div>
              ) : filteredAndSortedFirms.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-4">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <p className="text-gray-500 text-sm">No firms match your current filters</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your search criteria or add a new firm</p>
                  <button onClick={handleAddClick} className="mt-4 px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">+ Add Firm</button>
                </div>
              ) : (
                <>
                  {/* List block — same treatment full-width or rail; only the width changes */}
                  <div className={`flex-1 min-h-0 mb-1 bg-[color:var(--list-block-bg)] border rounded-lg shadow-sm overflow-hidden flex flex-col ${selectedFirmId ? 'ml-3 mr-1' : 'mx-3'}`}>
                    <FirmList
                      firms={filteredAndSortedFirms}
                      compact={!!selectedFirmId}
                      selectedId={selectedFirmId}
                      onSelect={(firm) => setSelectedFirmId(firm.id)}
                      onEditClick={selectedFirmId ? undefined : handleEditClick}
                      selectedIds={selectedIds}
                      onToggleSelect={handleToggleSelect}
                    />
                  </div>
                  {/* Count footer — bottom-right */}
                  <div className="flex-shrink-0 px-4 py-1.5 flex justify-end">
                    <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                      <span className={selectedIds.size > 0 ? 'font-semibold text-emerald-600' : ''}>{selectedIds.size} selected</span>
                      <span className="mx-1 text-gray-300">/</span>
                      <span className="font-medium text-gray-500">{filteredAndSortedFirms.length}</span>
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Detail pane — only when a firm is selected; same block treatment as the list */}
            {selectedFirmId && (
              <div className="flex-1 min-w-0 min-h-0 mr-3 ml-1 mb-1 bg-white border rounded-lg shadow-sm overflow-hidden flex flex-col">
                <FirmDetail
                  firmId={selectedFirmId}
                  embedded
                  onExpand={handleExpandFirm}
                  onClose={() => setSelectedFirmId(null)}
                  onDeleted={() => { setSelectedFirmId(null); refetch?.(); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {modalOpen && (
        <FirmModal
          firm={editingFirm}
          onSave={handleSaveFirm}
          onClose={handleCloseModal}
          onDelete={editingFirm ? handleDeleteFirm : null}
          onImportCsv={() => { setModalOpen(false); setEditingFirm(null); setImportModalOpen(true); }}
        />
      )}

      {dedupModalOpen && (
        <DedupFirmsModal
          onClose={() => setDedupModalOpen(false)}
          onComplete={() => { setDedupModalOpen(false); refetch?.(); }}
        />
      )}

      {importModalOpen && (
        <ImportModal
          onClose={() => setImportModalOpen(false)}
          onImport={handleImport}
          existingFirms={firms}
        />
      )}
    </AppShell>
  );
};

export default FirmsPage;
